import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Users, Clock, AlertCircle, UserX, PlaneTakeoff, Clock3, Eye } from "lucide-react";
import { ROLES } from "@/constants/roles";
import AttendanceDetailDialog from "@/components/AttendanceDetailDialog";

const statusMap = {
  on_time: { label: "Tepat Waktu", cls: "bg-signal-ok text-white" },
  late: { label: "Terlambat", cls: "bg-signal-late text-white" },
};
function fmt(dt) { if (!dt) return "—"; return new Date(dt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); }

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/admin/attendance/${row.attendance_id}`);
      setDetail(data);
    } catch (_e) {
      setDetail(row);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/admin/stats"), api.get("/admin/attendance")]);
        setStats(s.data); setRows(a.data);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid={TESTIDS.adminDashboardPage} className="space-y-8">
      <div>
        <div className="label-tiny">Dashboard Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Ringkasan Hari Ini</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testid={TESTIDS.statTotal} label="Total Karyawan" value={stats?.total_users ?? "—"} icon={<Users className="w-5 h-5" />} />
        <StatCard testid={TESTIDS.statOnTime} label="Tepat Waktu" value={stats?.on_time_today ?? "—"} icon={<Clock className="w-5 h-5" />} color="text-signal-ok" />
        <StatCard testid={TESTIDS.statLate} label="Terlambat" value={stats?.late_today ?? "—"} icon={<AlertCircle className="w-5 h-5" />} color="text-signal-late" />
        <StatCard testid={TESTIDS.statAbsent} label="Belum Hadir" value={stats?.absent_today ?? "—"} icon={<UserX className="w-5 h-5" />} color="text-signal-absent" />
        <StatCard testid={TESTIDS.statPendingLeave} label="Cuti Menunggu" value={stats?.pending_leave ?? "—"} icon={<PlaneTakeoff className="w-5 h-5" />} color="text-klein" />
        <StatCard testid={TESTIDS.statPendingOvertime} label="Lembur Menunggu" value={stats?.pending_overtime ?? "—"} icon={<Clock3 className="w-5 h-5" />} color="text-klein" />
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between">
          <div className="label-tiny">Kehadiran Hari Ini</div>
          <div className="text-xs text-gray-500">{rows.length} entri</div>
        </div>
        <table data-testid={TESTIDS.todayTable} className="w-full text-sm min-w-[900px]">
          <thead className="bg-black text-white text-left">
            <tr><Th>Nama</Th><Th>Role</Th><Th>Lokasi</Th><Th>Check-in</Th><Th>Check-out</Th><Th>Status</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-gray-500">Belum ada absensi hari ini.</td></tr>}
            {rows.map((r) => {
              const s = statusMap[r.status] || { label: "-", cls: "bg-gray-200" };
              return (
                <tr key={r.attendance_id} className="border-t border-black/10 hover:bg-gray-50">
                  <Td className="font-semibold">{r.user_name}</Td>
                  <Td className="text-xs">{ROLES[r.user_role] || r.user_role}</Td>
                  <Td className="text-xs">{r.is_wfa ? <span className="text-klein font-semibold">WFA</span> : r.office_name}</Td>
                  <Td className="font-mono">{fmt(r.check_in_time)}</Td>
                  <Td className="font-mono">{fmt(r.check_out_time)}</Td>
                  <Td><span className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${s.cls}`}>{s.label}</span></Td>
                  <Td>
                    <button
                      data-testid={`view-detail-${r.attendance_id}`}
                      onClick={() => openDetail(r)}
                      className="inline-flex items-center gap-1 border border-black/20 hover:bg-black hover:text-white px-2 py-1 text-xs font-semibold transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" /> Detail
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AttendanceDetailDialog record={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}
function StatCard({ label, value, icon, color = "text-black", testid }) {
  return (
    <div data-testid={testid} className="border border-black/10 bg-white p-5">
      <div className="flex items-center justify-between"><div className="label-tiny">{label}</div><span className={color}>{icon}</span></div>
      <div className={`font-display text-4xl font-black tracking-tighter mt-3 ${color}`}>{value}</div>
    </div>
  );
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
